import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Tile from '../../../../components/Tile/Tile';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download-icon.svg';
import { ReactComponent as OpenIcon } from '../../../../assets/icons/unfold-icon.svg';
import Popup from '../../../Popup/Popup';
import { exportElementToPdf } from '../../../../services/exportService';
import './Chart.css';

function Chart({component}) {
    const {
        header,
        subheader,
        chartType,
        xAxis,
        series,
        data,
        chartJson,
        summary,
        config // in case chart_type is under config
    } = component;

    // Helper to build chartJson from component props
    const buildChartJson = () => {
        // Determine chart type
        const type = chartType || "bar";
        // Extract labels from data using xAxis
        const labels = data && xAxis
            ? data.map(row => {
                // If row is an object, get xAxis property; if array, assume [label, value]
                if (typeof row === "object" && !Array.isArray(row)) {
                    return row[xAxis];
                } else if (Array.isArray(row)) {
                    return row[0];
                }
                return row;
            })
            : [];
        // Build datasets from series and data
        const datasets = (series || []).map((s, idx) => ({
            label: s.label || `Series ${idx + 1}`,
            data: data ? data.map(row => {
                // If row is an object, prefer selector (fallback to key) and try case variants
                if (typeof row === "object" && !Array.isArray(row)) {
                    const key = s.selector || s.key;
                    if (!key) return undefined;
                    if (key in row) return row[key];
                    const upper = typeof key === 'string' ? key.toUpperCase() : key;
                    const lower = typeof key === 'string' ? key.toLowerCase() : key;
                    return row[upper] ?? row[lower] ?? row[key];
                } else if (Array.isArray(row)) {
                    // Array rows come in as [xValue, series1, series2, ...]
                    return row[idx + 1];
                }
                return row;
            }) : [],
            backgroundColor: s.backgroundColor,
            borderColor: s.borderColor
        }));

        // Fallback if no series: single dataset from data
        if (datasets.length === 0 && data) {
            datasets.push({
                label: header || "Dataset",
                data: data.map(row => Array.isArray(row) ? row[1] : row),
            });
        }

        return {
            type,
            data: {
                labels,
                datasets
            }
        };
    };

    const [updatedChartJson, setUpdatedChartJson] = useState(chartJson);
    const containerRefInline = useRef(null);
    const chartRefInline = useRef(null);
    const chartRefPopup = useRef(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    useEffect(() => {
        if (chartJson) {
            setUpdatedChartJson(chartJson);
        } else {
            const builtJson = buildChartJson();
            setUpdatedChartJson(builtJson);
        }
    // Only run when chartJson, data, series, xAxis, chartType, or config changes
    }, [chartJson, data, series, xAxis, chartType, config]);

    const handleDownload = async () => {
        try {
            const target = containerRefInline.current;
            if (!target) return;
            const safeHeader = String(header || 'chart').replace(/[^a-z0-9\-\s_]/gi, '').replace(/\s+/g, '_').toLowerCase();
            target.classList.add('export-mode');
            // Expand content width before capture to reduce clipping on the right edge
            const inner = chartRefInline.current;
            const prevWidth = inner && inner.style.width;
            if (inner) inner.style.width = Math.max(inner.scrollWidth, inner.clientWidth) + 'px';
            await exportElementToPdf(target, {
                filename: `${safeHeader}.pdf`,
                pageOrientation: 'landscape',
                margin: 10,
                scale: 2,
            });
            if (inner) inner.style.width = prevWidth || '';
            target.classList.remove('export-mode');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to export chart to PDF', err);
        } finally {
            const target = containerRefInline.current;
            if (target) target.classList.remove('export-mode');
        }
    };

    const HeaderSection = () => (
        (header || subheader) ? (
            <div className="header header-with-actions">
                <div className="header-text-group">
                    <div className="header-text">{header}</div>
                    <div className="subheader-text">{subheader || (chartType ? chartType.charAt(0).toUpperCase() + chartType.slice(1) + " Chart" : "")}</div>
                </div>
                <div className="header-actions">
                    <DownloadIcon className="icon" onClick={handleDownload} />
                    <OpenIcon className="icon enlarge" onClick={() => setIsPopupOpen(true)} />
                </div>
            </div>
        ) : null
    );

    return (
        <div className="report-component chart" ref={containerRefInline}>
            <HeaderSection />
            <div className="content-container">
                <div className="component-content">
                    <div className="chart-json" ref={chartRefInline}>
                        <Tile chartJson={updatedChartJson} minW="100%" minH="300" disableAnimation={true} />
                    </div>
                    {summary && (
                        <div className="chart-text">
                            <div className="left-bar" />
                            <ReactMarkdown className="text-content">{summary}</ReactMarkdown>
                        </div>
                    )}
                </div>
            </div>
            {isPopupOpen && (
                <Popup
                    onClose={() => setIsPopupOpen(false)}
                    content={(
                        <div className="chart-popup-content" onClick={(e) => e.stopPropagation()}>
                            <div className="report-component chart">
                                <div className="header header-with-actions">
                                    <div className="header-text-group">
                                        <div className="header-text">{header}</div>
                                        <div className="subheader-text">{subheader || (chartType ? chartType.charAt(0).toUpperCase() + chartType.slice(1) + " Chart" : "")}</div>
                                    </div>
                                    <div className="header-actions">
                                        <span className="icon" role="button" aria-label="Close" title="Close" onClick={() => setIsPopupOpen(false)}>✕</span>
                                    </div>
                                </div>
                                <div className="content-container">
                                    <div className="component-content">
                                        <div className="chart-json" ref={chartRefPopup}>
                                            <Tile chartJson={updatedChartJson} minW="100%" minH="300" disableAnimation={true} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    );
}

export default Chart; 